import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { RealtimeChannel } from '@supabase/supabase-js'

// Define types directly to avoid Database type issues
type AccountCampaign = {
  id: string
  account_id: string
  name: string
  assistant_id: string | null
  trigger_type: string
  trigger_keywords: string[] | null
  tags: string[] | null
  pipeline_id: string | null
  pipeline_stage_id: string | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

type CampaignInsert = Omit<AccountCampaign, 'id' | 'created_at' | 'updated_at'>
type CampaignUpdate = Partial<Omit<AccountCampaign, 'id' | 'account_id' | 'created_at' | 'updated_at'>>

interface UseCampaignsReturn {
  campaigns: AccountCampaign[]
  loading: boolean
  refreshing: boolean
  error: string | null
  createCampaign: (data: Omit<CampaignInsert, 'account_id'>) => Promise<boolean>
  updateCampaign: (id: string, data: Partial<CampaignUpdate>) => Promise<boolean>
  deleteCampaign: (id: string) => Promise<boolean>
  toggleCampaignStatus: (id: string, isActive: boolean) => Promise<boolean>
  refetch: () => Promise<void>
}

export const useCampaigns = (): UseCampaignsReturn => {
  const { user } = useAuth()
  const [campaigns, setCampaigns] = useState<AccountCampaign[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const channelRef = useRef<RealtimeChannel | null>(null)

  const fetchCampaigns = async (silent = false): Promise<void> => {
    if (!user?.account_id || !supabase) {
      setLoading(false)
      return
    }

    try {
      if (!silent) {
        setLoading(true)
      } else {
        setRefreshing(true)
      }
      setError(null)

      const { data: campaignsData, error: campaignsError } = await supabase
        .from('account_campaigns')
        .select('*')
        .eq('account_id', user.account_id)
        .order('created_at', { ascending: false })

      if (campaignsError) throw campaignsError

      setCampaigns(campaignsData || [])
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar campanhas')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const createCampaign = async (data: Omit<CampaignInsert, 'account_id'>): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      console.log('➕ [Campaigns] Creating new campaign:', data)

      const { data: newCampaign, error } = await supabase
        .from('account_campaigns')
        .insert({ ...data, account_id: user.account_id })
        .select()
        .single()

      if (error) throw error

      console.log('✅ [Campaigns] Campaign created successfully:', newCampaign)

      setCampaigns(prev => [newCampaign, ...prev])

      setTimeout(() => {
        fetchCampaigns(true)
      }, 500)

      return true
    } catch (err: any) {
      console.error('❌ [Campaigns] Error creating campaign:', err)
      setError(err.message || 'Erro ao criar campanha')
      return false
    }
  }

  const updateCampaign = async (id: string, data: Partial<CampaignUpdate>): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    // Store original for rollback
    const originalCampaigns = [...campaigns]

    try {
      setError(null)

      // Optimistic update - update immediately in list
      setCampaigns(prev =>
        prev.map(campaign =>
          campaign.id === id
            ? { ...campaign, ...data, updated_at: new Date().toISOString() }
            : campaign
        )
      )

      const { error } = await supabase
        .from('account_campaigns')
        .update(data)
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar campanha')
      // Rollback optimistic update
      setCampaigns(originalCampaigns)
      return false
    }
  }

  const deleteCampaign = async (id: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    // Store original for rollback
    const originalCampaigns = [...campaigns]

    try {
      setError(null)

      // Optimistic update - remove immediately from list
      setCampaigns(prev => prev.filter(campaign => campaign.id !== id))

      const { error } = await supabase
        .from('account_campaigns')
        .delete()
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar campanha')
      // Rollback optimistic update
      setCampaigns(originalCampaigns)
      return false
    }
  }

  const toggleCampaignStatus = async (id: string, isActive: boolean): Promise<boolean> => {
    return await updateCampaign(id, { is_active: isActive })
  }

  const refetch = async (): Promise<void> => {
    await fetchCampaigns(true)
  }

  // Setup realtime subscription
  useEffect(() => {
    if (!user?.account_id || !supabase) return

    // Initial fetch
    fetchCampaigns()

    // Setup realtime subscription
    const channel = supabase
      .channel('account_campaigns_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_campaigns',
          filter: `account_id=eq.${user.account_id}`,
        },
        (payload) => {
          console.log('🔄 [Campaigns] Realtime event received:', payload.eventType, payload)

          if (payload.eventType === 'INSERT') {
            console.log('➕ [Campaigns] Adding new campaign via Realtime:', payload.new)
            setCampaigns((prev) => {
              const exists = prev.some((c) => c.id === payload.new.id)
              if (exists) {
                console.log('⚠️ [Campaigns] Campaign already exists, skipping')
                return prev
              }
              console.log('✅ [Campaigns] Campaign added to list')
              return [payload.new as AccountCampaign, ...prev]
            })
          } else if (payload.eventType === 'UPDATE') {
            console.log('✏️ [Campaigns] Updating campaign via Realtime:', payload.new)
            setCampaigns((prev) =>
              prev.map((campaign) =>
                campaign.id === payload.new.id ? (payload.new as AccountCampaign) : campaign
              )
            )
          } else if (payload.eventType === 'DELETE') {
            console.log('🗑️ [Campaigns] Deleting campaign via Realtime:', payload.old)
            setCampaigns((prev) => prev.filter((campaign) => campaign.id !== payload.old.id))
          }
        }
      )
      .subscribe((status, err) => {
        console.log('📡 [Campaigns] Realtime subscription status:', status, err)
        if (err) {
          console.error('❌ [Campaigns] Realtime subscription error:', err)
        }
      })

    channelRef.current = channel

    // Log when subscription is set up
    console.log('🚀 [Campaigns] Realtime subscription initialized for account:', user.account_id)

    // Cleanup subscription on unmount
    return () => {
      console.log('🧹 [Campaigns] Cleaning up Realtime subscription')
      if (channelRef.current && supabase) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [user?.account_id])

  return {
    campaigns,
    loading,
    refreshing,
    error,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    toggleCampaignStatus,
    refetch,
  }
}